import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiPackageService } from '@services/api-package.service';
import { ApiTransactionService } from '@services/api-transaction.service';
import { SharedService } from '@services/shared.service';
import { CouponModel } from '@shared/models/CouponModel';
import { BenefitsType, Country } from '@shared/models/Enums';
import { EventModel } from '@shared/models/EventModel';
import { GiftcardModel } from '@shared/models/GiftcardModel';
import { IBenefits } from '@shared/models/Interfaces';
import { PackageModel } from '@shared/models/PackageModel';
import { ProductModel } from '@shared/models/ProductModel';
import { ScratchModel } from '@shared/models/ScratchModel';
import { TransactionNotesModel, TransactionPackageModel } from '@shared/models/TransactionModel';

@Component({
    selector: 'app-pack-details',
    templateUrl: './pack-details.component.html',
    styleUrls: ['./pack-details.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-in', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('10ms ease-out', style({ opacity: 0 }))
            ])
        ])
    ]
})
export class PackDetailsComponent {

    @Input() transactionPackageId: number;
    @Input() country: Country = Country.CR;
    @Input() preloadedSale: TransactionPackageModel = null; //receive the data to dont call the api again
    @Output() preloadSale: EventEmitter<TransactionPackageModel> = new EventEmitter<TransactionPackageModel>();// send the data to preload it in the next reload
    @Output() onUpdateTransactionNote: EventEmitter<TransactionNotesModel> = new EventEmitter<TransactionNotesModel>(); 

    sale: TransactionPackageModel;
    showItems: boolean = false;

    showSendGivi: boolean = false;

    requireVerification = true;

    linkCopied = false;
    showBarcode: boolean = false;

    token = '';
    tokenTimerId = null;

    successRedeemId = null;

    // Note
    giviNote: TransactionNotesModel;
    transactionId: number;
    customerOwnerId: number;

    showMoreDetails = false;
    showAllDesc = false;

    actionOption = 1; // 1 MyPack | 2 Send Pack

    constructor(public sharedService: SharedService,
        public apiTransactionService: ApiTransactionService,
        public apiPackageService: ApiPackageService,
        public router: Router,
        private modalService: NgbModal,
    ) { }

    ngOnInit(): void {

        if (this.preloadedSale == null) this._getPackageTransaction(this.transactionPackageId);
        else this.sale = new TransactionPackageModel(this.preloadedSale);

    }

    onBenefitDetail(item: IBenefits) {
        this.sharedService.setShowBenefitDetailModal(item);
    }

    onShowItems() {
        this.showItems = !this.showItems;
    }

    openConfirmModal(modal) {

        this.modalService.open(modal, {
            scrollable: true, centered: true, windowClass: 'modal-full'
        });
    }

    activeTransactionPackage() {

        this.sharedService.setLoading(true);

        this.modalService.dismissAll();

        this.apiTransactionService.activeTransactionPackage(this.sale).then(
            (data: any) => {

                this.sharedService.setLoading(false);

                this.sharedService.showSuccessAlert(
                    null, `El pack fue activado correctamente`
                ).then(_ => {

                    let url = this.router.url;

                    this.router.navigateByUrl('/market', { skipLocationChange: true }).then(() => {
                        this.router.navigateByUrl(url);
                    });
                });
            },
            (err: any) => {
                this.sharedService.setLoading(false);

                const errMsg = 'No se pudo activar el paquete. Por favor volvé a intentarlo.';
                this.sharedService.showErrorAlert(null, errMsg);
            }
        );
    }

    showGiviNote(transactionNote: TransactionNotesModel, transactionId: number, customerOwnerId: number) {

        this.giviNote = transactionNote;
        this.transactionId = transactionId;
        this.customerOwnerId = customerOwnerId;
        this.sharedService.setShowGiviNoteModalModal(true);
    }

    onUpdateNote(transactionNote: TransactionNotesModel){

        this.sale.transactionNotes = transactionNote;
        this.onUpdateTransactionNote.emit(transactionNote);
    }

    private _getPackageTransaction(id: number): void {

        this.apiTransactionService.getFullPackageTransaction(id, this.country).then(
            (transaction) => {

                this.sale = new TransactionPackageModel({
                    ...transaction
                });

                this.sale.country = this.country;

                this._getFullPackage(this.sale.packageId);
            },
            (err) => {

                let errMsg = `Error al cargar información del Ticket`;
                if (err.status == 404) {
                    errMsg = `El Ticket no existe`;
                }
                // Desautorizado navigate to login
                else if (err.status == 401 || err.message?.indexOf('caducado') >= 0) {

                    this.sharedService.showErrorAlert(null, 'Error al cargar la información solicitada').then(()=>{
                        this.router.navigate(['/market']);
                    });
                    return;
                }
                this.sharedService.showErrorAlert(null, errMsg);
            }
        );
    }

    private _getFullPackage(packageId: number) {

        this.apiPackageService.getPackage(packageId).then(
            (selectedPackage: PackageModel) => {

                this.sale.package = selectedPackage;

                // this._setGeneralInfo();
                this._fillBenefits();
                // this._loadAccount();

            }
        ).catch(err => {

        });
    }

    private _fillBenefits() {

        //fill the interface with the corresponding values
        if (this.sale.package) {

            let vendors = this.sale.package.vendors;

            let packageBenefits: IBenefits[] = [];

            // Función para encontrar el vendor correspondiente
            const findVendor = (vendorId) => vendors.find(v => v.vendorId === vendorId) || null;


            // Rellenar beneficios
            if (this.sale.package.giftcards?.length > 0) {
                this.sale.package.giftcards.forEach(x => {
                    if (x.deletedAt == null) {
                        const giftcard = new GiftcardModel(x);
                        giftcard.packageVendor = findVendor(x.vendorId);
                        packageBenefits.push({
                            type: BenefitsType.Giftcard,
                            data: giftcard
                        });
                    }
                });
            }

            if (this.sale.package.coupons?.length > 0) {
                this.sale.package.coupons.forEach(x => {
                    if (x.deletedAt == null) {
                        const coupon = new CouponModel(x);
                        coupon.packageVendor = findVendor(x.vendorId);
                        packageBenefits.push({
                            type: BenefitsType.Coupon,
                            data: coupon
                        });
                    }

                });
            }

            if (this.sale.package.products?.length > 0) {
                this.sale.package.products.forEach(x => {
                    if (x.deletedAt == null) {
                        const product = new ProductModel(x);
                        product.packageVendor = findVendor(x.vendorId);
                        packageBenefits.push({
                            type: BenefitsType.Product,
                            data: product
                        });
                    }
                });
            }

            if (this.sale.package.events?.length > 0) {
                this.sale.package.events.forEach(x => {
                    if (x.deletedAt == null) {
                        const event = new EventModel(x);
                        event.packageVendor = findVendor(x.vendorId);
                        packageBenefits.push({
                            type: BenefitsType.Event,
                            data: event
                        });
                    }
                });
            }

            if (this.sale.package.scratches?.length > 0) {
                this.sale.package.scratches.forEach(x => {
                    if (x.deletedAt == null) {
                        const scratch = new ScratchModel(x);
                        scratch.thumbnailUrl = scratch.logoUrl;
                        scratch.packageVendor = findVendor(x.vendorId);
                        packageBenefits.push({
                            type: BenefitsType.Scratch,
                            data: scratch
                        });
                    }

                });
            }

            this.sale.package.packageBenefits = packageBenefits;

            this.preloadSale.emit(this.sale);
        }
    }
}
